import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';

/**
 * State for {@link ItemSelectionStore}.
 *
 * @remarks
 * `selectedItems` is modeled as a `Set` of opaque string ids (e.g. ROI ids in mosaic UIs).
 * Duplicates are ignored when adding; removal is idempotent for missing ids.
 */
export interface ItemSelectionStoreState {
  /** Currently selected item ids. */
  selectedItems: Set<string>;
  /**
   * When false, {@link selectItem} replaces the selection with one id and {@link deselectItem} clears it.
   * When true, {@link selectItem} unions one id and {@link deselectItem} removes only that id.
   */
  multiselectionEnabled: boolean;
}

/**
 * NgRx ComponentStore for a multiset of selected item ids, typically ROIs or gallery cells.
 *
 * @remarks
 * Prefer {@link selectItem} / {@link deselectItem} for user-driven toggles that respect
 * {@link ItemSelectionStoreState.multiselectionEnabled}; use {@link addSelectedItem} / {@link addSelectedItems} /
 * {@link removeSelectedItem} for incremental updates that ignore that flag; use {@link setSelectedItems} when
 * replacing the whole selection from a snapshot.
 *
 */
@Injectable()
export class ItemSelectionStore extends ComponentStore<ItemSelectionStoreState> {
  constructor() {
    super({
      selectedItems: new Set(),
      multiselectionEnabled: false,
    });
  }

  public readonly selectedItems$ = this.select((state) => state.selectedItems);

  public readonly selectedItemsArray$ = this.select((state) => Array.from(state.selectedItems));

  public readonly multiselectionEnabled$ = this.select((state) => state.multiselectionEnabled);

  public readonly setMultiselectionEnabled = this.updater((state, multiselectionEnabled: boolean) => ({
    ...state,
    multiselectionEnabled,
  }));

  /**
   * Replaces the entire selection in one update.
   *
   * @param selectedItems - New authoritative set; typically clone before mutating elsewhere.
   */
  public readonly setSelectedItems = this.updater((state, selectedItems: Set<string>) => ({
    ...state,
    selectedItems,
  }));

  /**
   * Adds a single id if not already present.
   *
   * @param item - Domain id to select.
   */
  public readonly addSelectedItem = this.updater((state, item: string) => ({
    ...state,
    selectedItems: new Set([...state.selectedItems, item]),
  }));

  /**
   * Union of the current selection with the given ids.
   *
   * @param items - Ids to add; order in the underlying `Set` is not specified.
   */
  public readonly addSelectedItems = this.updater((state, items: readonly string[]) => ({
    ...state,
    selectedItems: new Set([...state.selectedItems, ...items]),
  }));

  /**
   * Removes one id from the selection when present.
   *
   * @param item - Id to drop; no-op if already absent.
   */
  public readonly removeSelectedItem = this.updater((state, item: string) => ({
    ...state,
    selectedItems: new Set([...state.selectedItems].filter((i) => i !== item)),
  }));

  /**
   * Clears all selected ids.
   */
  public readonly clearSelectedItems = this.updater((state) => ({ ...state, selectedItems: new Set() }));

  /**
   * Selects one id; behavior depends on {@link ItemSelectionStoreState.multiselectionEnabled}.
   */
  public readonly selectItem = this.updater((state, item: string) =>
    state.multiselectionEnabled
      ? { ...state, selectedItems: new Set([...state.selectedItems, item]) }
      : { ...state, selectedItems: new Set([item]) }
  );

  /**
   * Deselects relative to one id; behavior depends on {@link ItemSelectionStoreState.multiselectionEnabled}.
   */
  public readonly deselectItem = this.updater((state, item: string) =>
    state.multiselectionEnabled
      ? { ...state, selectedItems: new Set([...state.selectedItems].filter((i) => i !== item)) }
      : { ...state, selectedItems: new Set() }
  );

  public readonly toggleItem = this.updater((state, item: string) =>
    state.selectedItems.has(item)
      ? state.multiselectionEnabled
        ? { ...state, selectedItems: new Set([...state.selectedItems].filter((i) => i !== item)) }
        : { ...state, selectedItems: new Set() }
      : state.multiselectionEnabled
      ? { ...state, selectedItems: new Set([...state.selectedItems, item]) }
      : { ...state, selectedItems: new Set([item]) }
  );

  /**
   * Returns the current selected items synchronously.
   *
   * @remarks
   * Delegates to the protected `ComponentStore.get()` to provide a public
   * synchronous snapshot of the selection (e.g. for drag-selection start).
   */
  public getSelectedItems(): Set<string> {
    return this.get((state) => state.selectedItems);
  }
}
